import PropTypes from 'prop-types';
import React, { useLayoutEffect } from 'react';
import { graphql } from 'gatsby';
import { css } from '@emotion/core'; // eslint-disable-line
import scriptLoader from 'react-async-script-loader';
import Layout from '../../organisms/Layout/Layout';
import HeroBanner from '../../organisms/HeroBanner/HeroBanner';
import BubbleCtaSection from '../../organisms/BubbleCtaSection/BubbleCtaSection';
import {
  colors,
  fontFamilies,
  unitConverter as uc,
} from '../../../styles/base';
import { Heading2 } from '../../atoms/Headings/Headings';
import './assets/css/csshake.css';

const shrinkSlideHeight = `max-height: ${uc('780px')}`;
const shrinkSlideWidth = `max-width: ${uc('780px')}`;
const slideSingleColumn = `max-width: ${uc('710px')}`;
const slideDateShrink = `max-width: ${uc('1050px')}`;
const slide1999FounderBubbles = `max-width: ${uc('770px')}`;
const slide1999Founders = `max-width: ${uc('1100px')}`;

const founderSlidesCss = css`
  height: 100%;
  overflow: hidden;

  /* slide specific css */
  .-polaroid-photo {
    position: relative;
    display: inline-block;
    overflow: hidden;
    background: ${colors.white};
    border: ${uc('20px')} solid ${colors.white};
    border-bottom-width: ${uc('70px')};
    box-shadow: ${uc('10px 10px 0')} rgba(0, 0, 0, 0.15);

    &::after {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 200%;
      margin-top: -50%;
      margin-left: -70%;
      background: rgba(255, 255, 255, 0.1);
      border-right: ${uc('90px')} transparent;
      transform: rotate(22deg);
      transform-origin: center center;
      content: '';
    }

    @media (${shrinkSlideWidth}) {
      width: ${uc('200px')};
      border-width: ${uc('15px')};
      border-bottom-width: ${uc('45px')};
      box-shadow: ${uc('7px 7px 0')} rgba(0, 0, 0, 0.15);
    }

    @media (${slideSingleColumn}) {
      border-width: ${uc('10px')};
      border-bottom-width: ${uc('30px')};
      box-shadow: ${uc('5px 5px 0')} rgba(0, 0, 0, 0.15);
    }
  }

  /* year 1999 */
  section.-year-1999 {
    @media (${slideSingleColumn}) {
      margin-bottom: ${uc('200px')};
    }

    .founder-slide__fiesta-flags {
      position: absolute;
      top: 0;
      left: 0;

      @media (${slide1999Founders}) {
        width: 42vw;
      }
    }

    .founder-slide__founder-bubble {
      display: table;
      float: right;
      clear: right;

      @media (${slide1999FounderBubbles}) {
        float: left;
        clear: left;
      }

      @media (${slideSingleColumn}) {
        display: flex;
        align-items: center;
        float: right;
        clear: right;
        margin-bottom: ${uc('15px')} !important;
      }

      &.-adam-miller {
        margin: ${uc('0 90px -10px 0')};

        @media (${slide1999FounderBubbles}) {
          margin-right: 0;
        }
      }

      &.-perry-wallack {
        margin-right: ${uc('175px')};

        @media (${slide1999Founders}) {
          margin-right: 0;
        }
      }

      &.-steven-seymour {
        margin-right: ${uc('75px')};

        @media (${shrinkSlideWidth}) {
          margin-top: ${uc('-20px')};
          margin-right: ${uc('75px')};
        }

        @media (${slide1999FounderBubbles}) {
          margin-top: 0;
          margin-right: 0;
        }
      }
    }

    .founder-slide__founder-name {
      margin-bottom: 0;
      color: ${colors.white};
      font-weight: bold;
      font-size: ${uc('15px')};
    }

    .founder-slide__founder-title {
      font-size: ${uc('16px')};
    }

    .founder-slide__adam-el-cholo {
      position: relative;
      display: inline-block;

      @media (max-width: ${uc('1320px')}) {
        width: ${uc('270px')};
      }

      @media (${shrinkSlideWidth}) {
        width: ${uc('200px')};
      }
    }

    .founder-slide__adam-perry {
      position: relative;
      top: ${uc('-10px')};

      @media (${slide1999Founders}) {
        top: ${uc('40px')};
      }
    }

    .founder-slide__el-cholo {
      position: absolute;
      bottom: ${uc('-71px')};
      left: ${uc('190px')};

      @media (${shrinkSlideWidth}) {
        bottom: ${uc('-31px')};
        width: ${uc('160px')};
      }

      @media (${slide1999Founders}) {
        top: 0;
        bottom: auto;
        width: ${uc('160px')};
        transform: rotate(10deg);
      }
    }

    .founder-slide__bowling-items {
      position: relative;
      display: inline-block;
      width: ${uc('300px')};
      height: ${uc('200px')};

      @media (max-width: ${uc('1320px')}) {
        width: ${uc('240px')};
      }
    }

    .founder-slide__bowling-pin {
      position: absolute;
      bottom: 0;
      left: ${uc('20px')};

      &:nth-of-type(2) {
        left: ${uc('80px')};
      }

      @media (max-width: ${uc('1320px')}) {
        width: ${uc('35px')};
      }
    }

    .founder-slide__bowling-ball {
      position: absolute;
      right: 0;
      bottom: 0;

      @media (max-width: ${uc('1320px')}) {
        width: ${uc('65px')};
      }
    }
  }

  /* year 2000 */
  .-year-2000 {
    margin-bottom: ${uc('200px')} !important;

    @media (${slideSingleColumn}) {
      margin-bottom: 0 !important;
      padding-bottom: ${uc('100px')};
    }

    .founder-slide__aol {
      display: inline-block;
      vertical-align: top;

      @media (max-width: ${uc('1465px')}) {
        width: ${uc('170px')};
      }
    }

    .founder-slide__flying-money-container {
      position: relative;
      display: inline-block;
      width: ${uc('300px')};
      height: ${uc('200px')};
      margin-left: ${uc('30px')};
    }

    .founder-slide__flying-money {
      position: absolute;
    }

    .founder-slide__flying-money.-one {
      top: 0;
      left: ${uc('70px')};
    }

    .founder-slide__flying-money.-two {
      top: ${uc('110px')};
      width: ${uc('60px')};
      transform: rotate(-10deg);

      @media (${shrinkSlideWidth}) {
        top: ${uc('80px')};
      }
    }

    .founder-slide__flying-money.-three {
      top: ${uc('160px')};
      width: ${uc('100px')};

      @media (${shrinkSlideWidth}) {
        top: ${uc('120px')};
      }
    }

    .founder-slide__flying-money.-four {
      top: ${uc('130px')};
      left: ${uc('110px')};
      width: ${uc('100px')};
    }

    .founder-slide__man-on-books {
      @media (${slideSingleColumn}) {
        display: none;
      }
    }
  }

  /* year 2001 */
  section.-year-2001 {
    margin-bottom: 0 !important;

    .founder-slide__liberty-container {
      position: relative;
      top: ${uc('100px')};
      display: inline-block;

      @media (${slideSingleColumn}) {
        top: 0;
        padding-bottom: ${uc('50px')};
      }
    }

    .founder-slide__statue-of-liberty-statue {
      position: absolute;
      bottom: ${uc('39px')};
      left: 20%;
      z-index: 1;

      @media (${slideSingleColumn}) {
        left: 25%;
        height: ${uc('250px')};
      }
    }

    .founder-slide__statue-of-liberty-background {
      position: relative;

      @media (${slideSingleColumn}) {
        height: ${uc('210px')};
      }
    }

    .founder-slide__statue-of-liberty {
      position: relative;
      top: ${uc('30px')};
      left: ${uc('50px')};
    }

    .founder-slide__right-contents.-year-2001 {
      @media (${slideSingleColumn}) {
        margin-top: ${uc('60px')};
        text-align: right;
      }
    }
  }

  /* years 2001-2005 */
  section.-year-2001-2005 {
    @media (${slideSingleColumn}) {
      margin-bottom: 0 !important;
      padding-bottom: ${uc('100px')};
    }

    .founder-slide__client-funnel {
      box-sizing: border-box;
      width: ${uc('372px')};
      height: ${uc('400px')};
      margin-left: auto;
      padding-top: ${uc('40px')};
      text-align: center;
      background-image: url(/company/founders/funnel.png);

      @media (${slideSingleColumn}) {
        width: ${uc('270px')};
        background-repeat: no-repeat;
        background-size: contain;
      }
    }

    .founder-slide__company {
      margin-bottom: ${uc('20px')};
    }

    .founder-slide__stacks-of-cash--2001-2005 {
      position: relative;
      top: ${uc('20px')};
      left: ${uc('30px')};

      @media (${slideSingleColumn}) {
        position: absolute;
        top: auto;
        bottom: ${uc('30px')};
      }
    }
  }

  /* years 2002-2007 */
  section.-year-2002-2007 {
    padding-bottom: ${uc('100px')};

    @media (${slideSingleColumn}) {
      margin-bottom: 0 !important;
    }

    .founder-slide__left-contents {
      @media (${slideSingleColumn}) {
        padding-bottom: ${uc('150px')};
        text-align: right;
      }
    }

    .founder-slide__adam-2002 {
      top: ${uc('59px')};
      left: ${uc('80px')};
      z-index: 1;

      @media (${shrinkSlideWidth}) {
        width: ${uc('200px')};
      }

      @media (max-width: ${uc('1345px')}) {
        left: ${uc('-30px')};
      }

      @media (${slideSingleColumn}) {
        top: 0;
        right: ${uc('40px')};
        left: auto;
        display: inline-block;
        width: ${uc('200px')};
      }
    }

    .founder-slide__kirsten-2002 {
      @media (${shrinkSlideWidth}) {
        width: ${uc('200px')};
      }

      @media (${slideSingleColumn}) {
        top: ${uc('-40px')};
        right: 0;
        display: inline-block;
        width: ${uc('200px')};
      }
    }

    .founder-slide__stacks-of-cash-container {
      position: relative;
      left: ${uc('-20px')};

      @media (${slideSingleColumn}) {
        position: absolute;
        bottom: ${uc('30px')};
        z-index: 1;
        text-align: center;
      }
    }

    .founder-slide__stacks-of-cash {
      &:nth-of-type(2) {
        top: ${uc('22px')};
        left: ${uc('-55px')};
      }

      &:nth-of-type(3) {
        top: ${uc('47px')};
        left: ${uc('-190px')};
      }

      @media (${slideSingleColumn}) {
        display: inline-block;
        width: 38%;

        &:nth-of-type(3) {
          width: 38%;
        }
      }
    }
  }

  /* stop here */

  /* year 2007 */
  section.-year-2007 {
    margin-bottom: ${uc('300px')};

    @media (${slideSingleColumn}) {
      margin-bottom: 0 !important;
    }

    .founder-slide__bessemer {
      @media (${slideSingleColumn}) {
        float: right;
      }
    }

    .founder-slide__mountain-of-cash {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      margin-right: auto;
      margin-left: auto;
    }

    .founder-slide__mountain-of-cash.-shake {
      animation: shake 0.5s ease-in-out;
    }

    .founder-slide__mountain-of-cash.-shake-little {
      animation: shake-little 0.5s ease-in-out;
    }
  }

  /* year 2008 */
  section.-year-2008 {
    margin-bottom: ${uc('800px')};
    background-image: url(/company/founders/world-map.png);
    background-repeat: no-repeat;
    background-position: center center;

    @media (${slideSingleColumn}) {
      padding-bottom: ${uc('100px')};
    }

    .founder-slide__contents {
      @media (${slideSingleColumn}) {
        padding-bottom: ${uc('150px')};
      }
    }

    .founder-slide__wall-of-genius {
      position: absolute;
      top: ${uc('240px')};
      left: ${uc('170px')};
      z-index: 0;

      @media (max-width: ${uc('1465px')}) {
        display: none;
      }

      @media (${shrinkSlideWidth}) {
        margin-top: ${uc('-90px')};
      }

      @media (${slideSingleColumn}) {
        display: none;
      }
    }

    .founder-slide__photo-of-employees {
      z-index: 1;

      @media (${shrinkSlideWidth}) {
        width: ${uc('300px')};
      }

      @media (${slideSingleColumn}) {
        float: right;
        width: 90%;
        margin-top: ${uc('30px')};
      }
    }

    .founder-slide__adp {
      @media (${slideSingleColumn}) {
        float: right;
        width: ${uc('140px')};
      }
    }

    .founder-slide__lots-of-people-container {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      height: ${uc('200px')};
      perspective: ${uc('50px')};
      perspective-origin: 50% -80%;

      @media (${slideSingleColumn}) {
        height: ${uc('128px')};
      }
    }

    .founder-slide__lots-of-people {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      width: 100%;
      height: ${uc('168px')};
      background: url(/company/founders/people--year-2008.png);

      @media (${shrinkSlideWidth}) {
        height: ${uc('115px')};
        background-size: ${uc('200px')} auto;
      }

      @media (${slideSingleColumn}) {
        height: ${uc('128px')};
        background-size: auto 128px;
      }

      &:nth-of-type(2) {
        transform: translateZ(${uc('-30px')});
      }

      &:nth-of-type(3) {
        transform: translateZ(${uc('-40px')});
      }

      &:nth-of-type(4) {
        transform: translateZ(${uc('-45px')});
      }

      &:nth-of-type(5) {
        transform: translateZ(${uc('-53px')});
      }

      &:nth-of-type(6) {
        transform: translateZ(${uc('-62px')});
      }

      &:nth-of-type(7) {
        transform: translateZ(${uc('-71px')});
      }
    }
  }

  /* year 2009 */
  section.-year-2009 {
    background-image: url(/company/founders/acdc-background.jpg);
    background-size: cover;

    .founder-slide__left-contents {
      position: relative;
      margin-top: ${uc('30px')};
      text-align: right;
    }

    .founder-slide__steven-acdc {
      top: ${uc('60px')};
      left: ${uc('80px')};
      z-index: 1;

      @media (${slideSingleColumn}) {
        top: 0;
        left: auto;
        width: ${uc('200px')};
        transform: rotate(-7deg);
      }
    }

    .founder-slide__acdc-shirt {
      @media (${slideSingleColumn}) {
        width: ${uc('200px')};
        transform: rotate(3deg);
      }
    }

    .founder-slide__acdc-logo {
      position: relative;
      left: ${uc('20px')};
    }

    .founder-slide__acdc-bell {
      position: absolute;
      right: 0;
      left: ${uc('250px')};
      z-index: 2;
      width: ${uc('200px')};
      margin: auto;

      @media (${slideSingleColumn}) {
        right: auto;
        left: 0;
        width: ${uc('260px')};
      }
    }
  }

  /* year 2011 */
  section.-year-2011 {
    position: relative;
    margin-bottom: 0;

    @media (${slideSingleColumn}) {
      height: ${uc('940px')};
    }

    .founder-slide__contents {
      box-sizing: border-box;
      padding-right: ${uc('70px')};

      @media (max-width: ${uc('977px')}) {
        padding-right: 0;
      }
    }

    .founder-slide__ipo-photos {
      position: relative;
      display: inline-block;

      @media (${slideSingleColumn}) {
        width: 100vw;
      }
    }

    .founder-slide__times-square {
      top: ${uc('56px')};
      left: ${uc('70px')};

      @media (max-width: ${uc('977px')}) {
        position: absolute;
        top: ${uc('226px')};
        left: ${uc('-40px')};
        z-index: 1;
      }

      @media (${slideSingleColumn}) {
        top: ${uc('186px')};
        left: ${uc('20px')};
        z-index: 1;
        width: ${uc('180px')};
        transform: rotate(-3deg);
      }
    }

    .founder-slide__csod-founders-2011 {
      top: ${uc('56px')};
      left: ${uc('-95px')};
      z-index: 1;

      @media (max-width: ${uc('1465px')}) {
        display: none;
      }
    }

    .founder-slide__steven-candid {
      left: ${uc('-120px')};

      @media (max-width: ${uc('1465px')}) {
        top: ${uc('50px')};
        left: ${uc('-20px')};
      }

      @media (max-width: ${uc('977px')}) {
        position: absolute;
        top: ${uc('210px')};
        left: auto;
      }

      @media (${slideSingleColumn}) {
        left: ${uc('175px')};
        width: ${uc('180px')};
        transform: rotate(10deg);
      }
    }

    .founder-slide__nasdaq-bell {
      position: relative;
      z-index: 2;

      @media (${shrinkSlideWidth}) {
        width: ${uc('400px')};
      }

      @media (${slideSingleColumn}) {
        width: 82%;
      }
    }
  }

  .timeline--start-point,
  .timeline--end-point {
    position: absolute;
    left: 50%;
    display: inline-block;
    height: 100%;
    transform: translateX(-50%);

    @media (${slideSingleColumn}) {
      right: ${uc('10px')};
      left: auto;
    }
  }

  .timeline--end-point {
    top: 0;
    height: auto;
    transition: 0.3s;
  }

  .timeline--end-point.-inactive {
    top: ${uc('-55px')};
  }

  .timeline__disc {
    width: ${uc('41px')};
    height: ${uc('41px')};
    border-radius: 50%;
    box-shadow: inset ${uc('0 0 0 14px')} white;

    @media (${slideSingleColumn}) {
      width: ${uc('31px')};
      height: ${uc('31px')};
      box-shadow: inset ${uc('0 0 0 10px')} white;
    }
  }

  .timeline__start-line,
  .timeline__end-line {
    width: ${uc('2px')};
    height: ${uc('10px')};
    margin: auto;
    background: ${colors.white};
  }

  .timeline__start-line {
    height: calc(100% - 144px);
  }
`;

const founderSlideCss = css`
  position: relative;
  top: 0 !important;
  z-index: 1;
  width: 100%;
  height: 100vh;
  margin-bottom: ${uc('100px')};
  padding: ${uc('118px 0 0 0')};
  overflow: hidden;
  text-align: center;

  @media (${shrinkSlideHeight}) {
    padding: ${uc('78px 0 0 0')};
  }

  @media (${slideSingleColumn}) {
    height: auto;
    min-height: 100vh;
  }

  &.-year-1999 {
    padding-top: ${uc('65px')};
    background: ${colors.primary};
  }

  &.-year-2000 {
    background: #f67160;
  }

  &.-year-2001 {
    background: #f7935f;
  }

  &.-year-2001-2005 {
    background: #9a587b;
  }

  &.-year-2002-2007 {
    background: #8788d3;
  }

  &.-year-2007 {
    background: #4289d1;
  }

  &.-year-2008 {
    background: #4bafc4;
  }

  &.-year-2009 {
    background: #59e8a3;
  }

  &.-year-2011 {
    background: #39c5e9;
  }
`;

const bubbleCTACss = css`
  background: #39c5e9;
`;

const heading2Css = css`
  position: relative;
  margin: auto;
  margin-bottom: ${uc('20px')};
  color: ${colors.white};
  font-size: ${uc('35px')};

  @media (${slideSingleColumn}) {
    margin-right: ${uc('70px')};
    text-align: right;
  }
`;

const dateCss = css`
  position: relative;
  margin: auto;
  margin-bottom: ${uc('40px')};
  color: ${colors.white};
  font-size: ${uc('75px')};
  font-family: ${fontFamilies.roboto};

  @media (${slideDateShrink}) {
    font-size: ${uc('60px')};
  }

  @media (${slideSingleColumn}) {
    margin-right: ${uc('70px')};
    font-size: ${uc('40px')};
    text-align: right;
  }
`;

const contentCss = css`
  display: table;
  width: 100%;
  color: ${colors.white};

  @media (${slideSingleColumn}) {
    display: flex;
    flex-direction: column;
  }

  .founder-slide__left-contents,
  .founder-slide__right-contents {
    display: table-cell;
    width: 50%;
    padding-top: 10vh;
    vertical-align: middle;

    @media (${shrinkSlideHeight}) {
      vertical-align: top;
    }

    @media (${slideSingleColumn}) {
      display: block;
      box-sizing: border-box;
      width: 100%;
      padding: ${uc('0 70px 0 30px')};
    }
  }

  .founder-slide__left-contents {
    padding-right: ${uc('50px')};
    padding-left: ${uc('30px')};
    text-align: right;

    @media (${slideSingleColumn}) {
      padding: ${uc('0 70px 0 30px')};
      text-align: left;

      .-year-2000 &,
      .-year-2001-2005 &,
      .-year-2002-2007 &,
      .-year-2007 &,
      .-year-2008 &,
      .-year-2009 &,
      .-year-2011 & {
        order: 2;
      }
    }
  }

  .founder-slide__right-contents {
    padding-right: ${uc('30px')};
    padding-left: ${uc('50px')};
    text-align: left;

    @media (${slideSingleColumn}) {
      padding: ${uc('0 70px 0 30px')};

      .-year-2000 &,
      .-year-2001-2005 &,
      .-year-2002-2007 &,
      .-year-2007 &,
      .-year-2008 &,
      .-year-2009 &,
      .-year-2011 {
        order: 1;
      }
    }
  }

  .founder-slide__content-heading {
    margin-bottom: ${uc('16px')};
    color: ${colors.white};
    font-weight: bold;
    font-size: ${uc('18px')};
    font-family: ${fontFamilies.roboto};
    text-transform: uppercase;

    @media (${slideSingleColumn}) {
      text-align: right;
    }
  }

  .founder-slide__text {
    max-width: ${uc('540px')};
    margin-bottom: ${uc('40px')};
    font-size: ${uc('16px')};

    .founder-slide__right-contents & {
      margin-left: 0;
    }

    @media (${slideSingleColumn}) {
      padding: ${uc('10px')};
      text-align: right;
    }
  }

  .founder-slide__founder-photo {
    display: table-cell;
    margin-right: ${uc('12px')};

    @media (${shrinkSlideHeight}) {
      width: ${uc('80px')};
    }

    @media (${slide1999FounderBubbles}) {
      width: ${uc('80px')};
      height: ${uc('80px')};
    }

    @media (${slideSingleColumn}) {
      order: 2;
      margin: ${uc('0 0 0 12px')};
    }
  }

  .founder-slide__founder-text {
    display: table-cell;
    text-align: left;
    vertical-align: middle;

    @media (${slideSingleColumn}) {
      text-align: right;
    }
  }
`;

const foundersBannerCss = css`
  padding-top: ${uc('150px')};
  background-color: ${colors.primary};
  background-position: 50% 0;

  img {
    height: 100%;
    opacity: 0.5;
    object-fit: cover;
  }

  .text-block {
    padding: ${uc('120px 25px 240px')};
  }

  div {
    margin-top: ${uc('100px')};

    @media (max-width: ${uc('780px')}) {
      margin-top: ${uc('70px')};
    }

    @media (max-width: ${uc('460px')}) {
      margin-top: ${uc('30px')};
    }
  }
`;

const FoundersPage = ({
  data,
  isScriptLoaded,
  isScriptLoadSucceed,
  location: { href },
}) => {
  // unpublished pages return null data
  if (!data) {
    return null;
  }

  const foundersPageProps = {
    id: data.sanityFoundersPage._id,
    metadata: { ...data.sanityFoundersPage._rawRoute, href },
    banner: data.sanityFoundersPage._rawBanner,
    bubbleCtaSection: data.sanityFoundersPage._rawBubbleCtaSection,
  };

  // use react hook to have JS init after dom renders so that classes exists
  useLayoutEffect(() => {
    if (isScriptLoaded) {
      // load finished
      if (isScriptLoadSucceed) {
        import('./assets/js/founders')
          .then(foundersJS => {
            foundersJS.default.init();
          })
          .catch(error => console.error(error)); // eslint-disable-line no-console
      } else {
        throw new Error('Failed to load Founders Page scripts/dependencies');
      }
    }
  });

  return (
    <Layout
      menuColor="white"
      sanityId={foundersPageProps.id}
      {...foundersPageProps.metadata}
    >
      <HeroBanner {...foundersPageProps.banner} css={foundersBannerCss} />
      <div css={founderSlidesCss}>
        <section css={founderSlideCss} className="-year-1999">
          <img
            src="/company/founders/fiesta-flags.png"
            alt="Color flags on a line"
            className="founder-slide__fiesta-flags"
          />
          <Heading2 css={heading2Css}>the idea</Heading2>
          <div css={dateCss}>Nov 8 1999</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <h3 className="founder-slide__content-heading">
                Company Kick-off Meeting
              </h3>
              <p className="founder-slide__text">
                We planned to kick-off operations at a ranch, but it started
                raining (a rare occurance in LA). The meeting was moved to a
                bowling alley, followed by dinner at El Cholo, a local Mexican
                restaurant.
              </p>
              <div className="founder-slide__bubbles">
                <div className="founder-slide__founder-bubble -adam-miller">
                  <img
                    src="/company/founders/adam-1999.png"
                    alt=""
                    className="founder-slide__founder-photo"
                  />
                  <div className="founder-slide__founder-text">
                    <h4 className="founder-slide__founder-name">Adam Miller</h4>
                    <span className="founder-slide__founder-title">
                      Mastermind, CEO
                    </span>
                  </div>
                </div>
                <div className="founder-slide__founder-bubble -perry-wallack">
                  <img
                    src="/company/founders/perry-1999.png"
                    alt=""
                    className="founder-slide__founder-photo"
                  />
                  <div className="founder-slide__founder-text">
                    <h4 className="founder-slide__founder-name">
                      Perry Wallack
                    </h4>
                    <span className="founder-slide__founder-title">
                      Money man, CFO
                    </span>
                  </div>
                </div>
                <div className="founder-slide__founder-bubble -steven-seymour">
                  <img
                    src="/company/founders/seymour-1999.png"
                    alt=""
                    className="founder-slide__founder-photo"
                  />
                  <div className="founder-slide__founder-text">
                    <h4 className="founder-slide__founder-name">
                      Steven Seymour
                    </h4>
                    <span className="founder-slide__founder-title">
                      Entertainer, SVP Sales
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div className="founder-slide__right-contents">
              <div className="founder-slide__adam-el-cholo">
                <img
                  src="/company/founders/adam-perry-photo.png"
                  alt="Adam Miller, Perry Wallack after flying into LA"
                  className="founder-slide__adam-perry"
                />
                <img
                  src="/company/founders/el-cholo-logo.png"
                  alt="El Cholo"
                  className="founder-slide__el-cholo"
                />
              </div>
              <div className="founder-slide__bowling-items">
                <img
                  src="/company/founders/bowling-pin.png"
                  alt="Bowling Pin"
                  className="founder-slide__bowling-pin -one-pin"
                />
                <img
                  src="/company/founders/bowling-pin.png"
                  alt="Bowling Pin"
                  className="founder-slide__bowling-pin -two-pin"
                />
                <img
                  src="/company/founders/bowling-ball.png"
                  alt="Bowling Ball"
                  className="founder-slide__bowling-ball"
                />
              </div>
            </div>
          </div>
        </section>
        <section css={founderSlideCss} className="-year-2000">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <Heading2 css={heading2Css}>
            walked away from stability to follow the dream
          </Heading2>
          <div css={dateCss}>2000</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <img
                className="founder-slide__man-on-books"
                src="/company/founders/man-on-books.png"
                alt="Man with spyglass standing on pile of books"
              />
            </div>
            <div className="founder-slide__right-contents">
              <h3 className="founder-slide__content-heading">
                Opportunity at AOL
              </h3>
              <p className="founder-slide__text">
                Walked away from a then-coveted opportunity to become the
                premier sponsor of AOL’s Research and Learning Center. The
                internet bubble burst a month later.
              </p>
              <div className="year-2000__imgs">
                <img
                  className="founder-slide__aol"
                  src="/company/founders/aol-logo.png"
                  alt="AOL logo"
                />
                <div className="founder-slide__flying-money-container">
                  <img
                    className="founder-slide__flying-money -one"
                    src="/company/founders/flying-money.png"
                    alt="Dollar signs with wings"
                  />
                  <img
                    className="founder-slide__flying-money -two"
                    src="/company/founders/flying-money--small.png"
                    alt="Dollar signs with wings"
                  />
                  <img
                    className="founder-slide__flying-money -three"
                    src="/company/founders/flying-money.png"
                    alt="Dollar signs with wings"
                  />
                  <img
                    className="founder-slide__flying-money -four"
                    src="/company/founders/flying-money.png"
                    alt="Dollar signs with wings"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
        <section css={founderSlideCss} className="-year-2001">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <Heading2 css={heading2Css}>started getting attention</Heading2>
          <div css={dateCss}>2001</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <h3 className="founder-slide__content-heading">
                Built a Pipeline of Prospective Clients
              </h3>
              <p className="founder-slide__text">
                Our online learning software was first ‘to the cloud.’ We built
                a pipeline primarily of Northeast financial services firms.
              </p>
            </div>
            <div className="founder-slide__right-contents">
              <div className="founder-slide__liberty-container">
                <img
                  className="founder-slide__statue-of-liberty-statue"
                  src="/company/founders/statue-of-liberty--statue.png"
                  alt="Statue of Liberty"
                />
                <img
                  className="founder-slide__statue-of-liberty-background"
                  src="/company/founders/statue-of-liberty--background.png"
                  alt="Statue of Liberty"
                />
              </div>
            </div>
          </div>
        </section>
        <section css={founderSlideCss} className="-year-2001-2005">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <Heading2 css={heading2Css}>closing deals</Heading2>
          <div css={dateCss}>2001-2005</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <div className="founder-slide__client-funnel">
                <img
                  src="/company/founders/smith-barney.png"
                  alt="Smith Barney logo"
                  className="founder-slide__company -smith-barney"
                />
                <img
                  src="/company/founders/aon-logo.png"
                  alt="AON logo"
                  className="founder-slide__company -aon"
                />
                <img
                  src="/company/founders/washington-mutual.png"
                  alt="Washington Mutual logo"
                  className="founder-slide__company -washington-mutual"
                />
              </div>
            </div>
            <div className="founder-slide__right-contents">
              <h3 className="founder-slide__content-heading">
                Hired Help and Closed Deals
              </h3>
              <p className="founder-slide__text">
                September 11th happened and instead of a hiring freeze, on
                September 15th we went on a hiring streak. We closed some big
                named clients, all from large enterprises.
              </p>
              <img
                src="/company/founders/stacks-of-cash.png"
                alt="Stacks of money"
                className="founder-slide__stacks-of-cash--2001-2005"
              />
            </div>
          </div>
        </section>
        <section css={founderSlideCss} className="-year-2002-2007">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <Heading2 css={heading2Css}>
            continued closing deals and servicing clients
          </Heading2>
          <div css={dateCss}>2002-2007</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <div className="founder-slide__adam-2002 -polaroid-photo">
                <img
                  src="/company/founders/adam--year-2002.jpg"
                  alt="Adam Miller in 2002 giving a presentation"
                  className="founder-slide__img"
                />
              </div>
              <div className="founder-slide__kirsten-2002 -polaroid-photo">
                <img
                  src="/company/founders/kirsten--year-2002.jpg"
                  alt="Kirsten Helvey in 2002"
                  className="founder-slide__img"
                />
              </div>
            </div>
            <div className="founder-slide__right-contents">
              <h3 className="founder-slide__content-heading">
                Closed Even More Deals
              </h3>
              <p className="founder-slide__text">
                We then had to figure out how to service all of these closed
                deals, and then build a sales team to rinse and repeat.
              </p>
              <div className="founder-slide__stacks-of-cash-container">
                <img
                  src="/company/founders/stacks-of-cash.png"
                  alt="Stacks of money"
                  className="founder-slide__stacks-of-cash"
                />
                <img
                  src="/company/founders/stacks-of-cash.png"
                  alt="Stacks of money"
                  className="founder-slide__stacks-of-cash"
                />
                <img
                  src="/company/founders/stacks-of-cash.png"
                  alt="Stacks of money"
                  className="founder-slide__stacks-of-cash"
                />
              </div>
            </div>
          </div>
        </section>
        <section css={founderSlideCss} className="-year-2007">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <Heading2 css={heading2Css}>in the money</Heading2>
          <div css={dateCss}>2007</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <img
                src="/company/founders/bessemer.png"
                alt="Bessemer logo"
                className="founder-slide__bessemer"
              />
            </div>
            <div className="founder-slide__right-contents">
              <h3 className="founder-slide__content-heading">
                Raised Venture Capital
              </h3>
              <p className="founder-slide__text">
                After too many no's to count, we finally raised our first
                institutional venture capital led by Bessemer Venture Partners.
              </p>
            </div>
          </div>
          <img
            src="/company/founders/giant-stack-of-cash.png"
            alt="Several piles of money"
            className="founder-slide__mountain-of-cash"
          />
        </section>
        <section css={founderSlideCss} className="-year-2008">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <Heading2 css={heading2Css}>grow fast as lightning</Heading2>
          <div css={dateCss}>2008</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <div className="founder-slide__photo-of-employees -polaroid-photo">
                <img
                  src="/company/founders/company-photo--year-2008.jpg"
                  alt=""
                  className=""
                />
              </div>
              <div className="founder-slide__wall-of-genius -polaroid-photo">
                <img
                  src="/company/founders/wall-of-genius--year-2008.jpg"
                  alt=""
                  className=""
                />
              </div>
            </div>
            <div className="founder-slide__right-contents">
              <h3 className="founder-slide__content-heading">
                Tripled the Size of the Company
              </h3>
              <p className="founder-slide__text">
                We began international expansion in EMEA and APAC. Closed even
                more deals including a global reseller deal with ADP.
              </p>
              <img
                src="/company/founders/adp-logo.png"
                alt="ADP company logo"
                className="founder-slide__adp"
              />
            </div>
            <div className="founder-slide__lots-of-people-container">
              <div className="founder-slide__lots-of-people" />
              <div className="founder-slide__lots-of-people" />
              <div className="founder-slide__lots-of-people" />
              <div className="founder-slide__lots-of-people" />
              <div className="founder-slide__lots-of-people" />
              <div className="founder-slide__lots-of-people" />
              <div className="founder-slide__lots-of-people" />
            </div>
          </div>
        </section>
        <section css={founderSlideCss} className="-year-2009">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <img
            className="founder-slide__acdc-bell"
            alt="ACDC Bell"
            src="/company/founders/acdc-bell.png"
          />
          <Heading2 css={heading2Css}>a little fun</Heading2>
          <div css={dateCss}>2009</div>
          <div className="timeline--start-point">
            <div className="timeline__disc" />
            <div className="timeline__start-line" />
          </div>
          <div css={contentCss}>
            <div className="founder-slide__left-contents">
              <div className="founder-slide__steven-acdc -polaroid-photo">
                <img
                  src="/company/founders/steven-acdc--year-2009.jpg"
                  alt="Steven at AC/DC concert"
                />
              </div>
              <div className="founder-slide__acdc-shirt -polaroid-photo">
                <img
                  src="/company/founders/acdc-shirt--yeaur-2009.jpg"
                  alt="AC/DC shirt"
                />
              </div>
            </div>
            <div className="founder-slide__right-contents">
              <h3 className="founder-slide__content-heading">
                AC/DC World Tour
              </h3>
              <p className="founder-slide__text">
                We took time out to tour the world following AC/DC. ‘For those
                about to learn, we salute you!‘
              </p>
              <img
                className="founder-slide__acdc-logo"
                alt="AC/DC logo"
                src="/company/founders/ac-dc.png"
              />
            </div>
          </div>
        </section>
        <section css={founderSlideCss} className="-year-2011">
          <div className="timeline--end-point">
            <div className="timeline__end-line" />
            <div className="timeline__disc" />
          </div>
          <Heading2 css={heading2Css}>graduation (aka IPO)</Heading2>
          <div css={dateCss}>2011</div>
          <div css={contentCss}>
            <p className="founder-slide__text">
              Completed a successful IPO led by Goldman Sachs. We were one of
              the top performing IPOs of the year.
            </p>
            <div className="founder-slide__ipo-photos">
              <div className="founder-slide__times-square -polaroid-photo">
                <img
                  src="/company/founders/nasdaq--year-2011.jpg"
                  alt="NASDAQ Times Square banner"
                />
              </div>
              <div className="founder-slide__nasdaq-bell -polaroid-photo">
                <img
                  src="/company/founders/nasdaq-bell--year-2001.jpg"
                  alt="Cornerstone founders ringing the opening bell"
                />
              </div>
              <div className="founder-slide__csod-founders-2011 -polaroid-photo">
                <img
                  src="/company/founders/csod-founders--year-2011.jpg"
                  alt="Cornerstone founders at NASDAQ podium"
                />
              </div>
              <div className="founder-slide__steven-candid -polaroid-photo">
                <img
                  src="/company/founders/steven--year-2011.jpg"
                  alt="Steven Seymour in a candid shot eating New York pizza"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
      {foundersPageProps.bubbleCtaSection && (
        <BubbleCtaSection
          {...foundersPageProps.bubbleCtaSection}
          className={bubbleCTACss}
        />
      )}
    </Layout>
  );
};

export const query = graphql`
  query($slug: String) {
    sanityFoundersPage(route: { slug: { current: { eq: $slug } } }) {
      _id
      _rawRoute(resolveReferences: { maxDepth: 100 })
      _rawBanner(resolveReferences: { maxDepth: 10 })
      _rawBubbleCtaSection(resolveReferences: { maxDepth: 100 })
    }
  }
`;

FoundersPage.propTypes = {
  data: PropTypes.shape({ sanityFoundersPage: {} }),
  isScriptLoaded: PropTypes.bool,
  isScriptLoadSucceed: PropTypes.bool,
  location: PropTypes.shape({ href: PropTypes.string }),
};

FoundersPage.defaultProps = {
  data: {},
  isScriptLoaded: false,
  isScriptLoadSucceed: false,
  location: { href: '' },
};

export default scriptLoader([
  'https://code.jquery.com/jquery-3.3.1.min.js',
  'https://cdnjs.cloudflare.com/ajax/libs/gsap/2.1.2/TweenMax.min.js',
])(FoundersPage);
